import { useEffect, useMemo, useState } from "react";
import { requestManualBackup, subscribeBackupRuns } from "../services/backupService";

const statusLabels = {
  requested: "Başlatılıyor",
  running: "Yedek alınıyor",
  success: "Başarılı",
  error: "Hatalı",
  missing: "Yedek alınmadı"
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("tr-TR");
};

const formatSize = (bytes) => {
  if (!bytes) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let size = Number(bytes);
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
};

function BackupStatusView({ themeMode = "light", currentUserEmail = "" }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [message, setMessage] = useState("");
  const isDark = themeMode === "dark";
  const colors = {
    pageText: isDark ? "#e5edf6" : "#0f172a",
    muted: isDark ? "#94a3b8" : "#64748b",
    card: isDark ? "#162235" : "#ffffff",
    soft: isDark ? "#101b2d" : "#f8fafc",
    border: isDark ? "#27364d" : "#e2e8f0",
    primary: isDark ? "#155e75" : "#124254"
  };

  useEffect(() => {
    const unsubscribe = subscribeBackupRuns((items) => {
      setRuns(items);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const latest = runs[0] || null;
  const statusStyle = useMemo(() => {
    const status = latest?.status;

    if (status === "success") {
      return {
        background: isDark ? "#123423" : "#dcfce7",
        color: isDark ? "#86efac" : "#166534",
        border: isDark ? "#166534" : "#bbf7d0"
      };
    }

    if (status === "error" || status === "missing") {
      return {
        background: isDark ? "#3a1414" : "#fee2e2",
        color: isDark ? "#fca5a5" : "#991b1b",
        border: isDark ? "#7f1d1d" : "#fecaca"
      };
    }

    return {
      background: isDark ? "#243247" : "#e0f2fe",
      color: isDark ? "#bfdbfe" : "#075985",
      border: isDark ? "#334155" : "#bae6fd"
    };
  }, [isDark, latest?.status]);

  const handleManualBackup = async () => {
    setRequesting(true);
    setMessage("");

    try {
      await requestManualBackup({ requestedBy: currentUserEmail });
      setMessage("Manuel yedek talebi alındı. Birkaç dakika içinde durum listede görünecek.");
    } catch (error) {
      setMessage(error.message || "Manuel yedek talebi oluşturulamadı.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div style={{ color: colors.pageText }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0 }}>Yedek Takibi</h1>
          <p style={{ margin: "8px 0 0", color: colors.muted, fontWeight: 700 }}>
            Günlük Firestore yedeklerinin alınıp alınmadığını ve hata durumlarını takip edin.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <div style={{ background: colors.soft, border: `1px solid ${colors.border}`, color: colors.pageText, borderRadius: 14, padding: "12px 14px", fontWeight: 900 }}>
            Otomatik yedek: Her gün 03:00
          </div>
          <button
            type="button"
            onClick={handleManualBackup}
            disabled={requesting}
            style={{
              border: "none",
              borderRadius: 14,
              padding: "12px 16px",
              background: colors.primary,
              color: "#fff",
              fontWeight: 900,
              cursor: requesting ? "not-allowed" : "pointer",
              opacity: requesting ? 0.72 : 1
            }}
          >
            {requesting ? "Talep ediliyor..." : "Şimdi Yedek Al"}
          </button>
        </div>
      </div>

      {message && (
        <div style={{ marginBottom: 16, padding: 14, borderRadius: 14, background: colors.soft, border: `1px solid ${colors.border}`, color: colors.pageText, fontWeight: 800 }}>
          {message}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1.2fr) repeat(3, minmax(180px, .8fr))", gap: 14, marginBottom: 22 }}>
        <div style={{ background: statusStyle.background, color: statusStyle.color, border: `1px solid ${statusStyle.border}`, borderRadius: 18, padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 900, opacity: 0.85 }}>Son Durum</div>
          <div style={{ fontSize: 30, fontWeight: 950, marginTop: 8 }}>
            {loading ? "Yükleniyor..." : statusLabels[latest?.status] || "Kayıt yok"}
          </div>
          {latest?.errorMessage && (
            <div style={{ marginTop: 10, fontSize: 13, fontWeight: 800, lineHeight: 1.45 }}>
              {latest.errorMessage}
            </div>
          )}
        </div>

        {[
          ["Son Başlama", formatDateTime(latest?.startedAt || latest?.createdAt)],
          ["Son Tamamlanma", formatDateTime(latest?.completedAt)],
          ["Yedek Konumu", latest?.outputUriPrefix || "-"]
        ].map(([label, value]) => (
          <div key={label} style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 18, padding: 18 }}>
            <div style={{ color: colors.muted, fontSize: 13, fontWeight: 900 }}>{label}</div>
            <div style={{ marginTop: 8, fontWeight: 900, lineHeight: 1.35, wordBreak: "break-word" }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 18, padding: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: 0 }}>Yedek Geçmişi</h2>
            <div style={{ marginTop: 6, color: colors.muted, fontWeight: 700 }}>
              Yetkili kullanıcı: {currentUserEmail || "-"}
            </div>
          </div>
          <span style={{ color: colors.muted, fontWeight: 900 }}>{runs.length} kayıt</span>
        </div>

        {loading ? (
          <div style={{ color: colors.muted, fontWeight: 800 }}>Yedek kayıtları yükleniyor...</div>
        ) : runs.length === 0 ? (
          <div style={{ color: colors.muted, fontWeight: 800 }}>Henüz yedek kaydı bulunmuyor.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr>
                  {["Dönem", "Durum", "Başlama", "Tamamlanma", "Süre", "Boyut", "Detay"].map((header) => (
                    <th key={header} style={{ textAlign: "left", padding: "12px 10px", color: colors.muted, borderBottom: `1px solid ${colors.border}` }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const rowStyle =
                    run.status === "success"
                      ? { color: isDark ? "#86efac" : "#166534" }
                      : run.status === "error" || run.status === "missing"
                      ? { color: isDark ? "#fca5a5" : "#991b1b" }
                      : { color: isDark ? "#bfdbfe" : "#075985" };

                  return (
                    <tr key={run.id}>
                      <td style={{ padding: "12px 10px", borderBottom: `1px solid ${colors.border}`, fontWeight: 900 }}>{run.dateKey || run.id}</td>
                      <td style={{ padding: "12px 10px", borderBottom: `1px solid ${colors.border}`, fontWeight: 900, ...rowStyle }}>
                        {statusLabels[run.status] || run.status || "-"}
                      </td>
                      <td style={{ padding: "12px 10px", borderBottom: `1px solid ${colors.border}` }}>{formatDateTime(run.startedAt || run.createdAt)}</td>
                      <td style={{ padding: "12px 10px", borderBottom: `1px solid ${colors.border}` }}>{formatDateTime(run.completedAt)}</td>
                      <td style={{ padding: "12px 10px", borderBottom: `1px solid ${colors.border}` }}>{run.durationSeconds ? `${run.durationSeconds} sn` : "-"}</td>
                      <td style={{ padding: "12px 10px", borderBottom: `1px solid ${colors.border}` }}>{formatSize(run.sizeBytes)}</td>
                      <td style={{ padding: "12px 10px", borderBottom: `1px solid ${colors.border}`, color: colors.muted, maxWidth: 320, wordBreak: "break-word" }}>
                        {run.errorMessage || run.outputUriPrefix || run.operationName || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default BackupStatusView;
